import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, MapPin, Users, ArrowLeft, ArrowUpRight, ListChecks, Mic } from 'lucide-react';
import axios from 'axios';
import Reveal from '../components/Reveal';
import { getEventAccent } from '../lib/eventStyle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventDetail = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API}/events/${id}`);
        setEvent(response.data);
      } catch (error) {
        console.error('Error fetching event:', error);
      }
      setLoading(false);
    };

    fetchEvent();
  }, [id]);

  if (loading) {
    return (
      <div className="dark-container min-h-screen flex items-center justify-center" data-testid="event-loading">
        <p className="body-large text-white">Loading event details...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="dark-container min-h-screen flex items-center justify-center" data-testid="event-not-found">
        <p className="body-large text-white">Event not found</p>
      </div>
    );
  }

  return (
    <div className="dark-container" data-testid="event-detail-page">
      {/* HERO IMAGE */}
      <section className="relative">
        <div className="relative h-[60vh] md:h-[72vh] min-h-[480px] overflow-hidden">
          <img src={event.image} alt={event.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black" />
          <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />

          {/* Back link */}
          <div className="absolute top-6 left-0 right-0">
            <div className="dark-content-container">
              <Link
                to="/events"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full glass-panel text-white/80 hover:text-[#00FFD1] hover:border-[#00FFD1]/40 transition-all text-sm font-medium"
                data-testid="event-back-link"
              >
                <ArrowLeft size={16} />
                Back to Events
              </Link>
            </div>
          </div>

          {/* Title */}
          <div className="absolute bottom-0 left-0 right-0 pb-12 md:pb-16">
            <div className="dark-content-container">
              <span className={`inline-block px-3 py-1.5 rounded-full ${getEventAccent(event.type).pill} text-xs font-semibold tracking-[0.2em] uppercase mb-5`}>
                {event.type}
              </span>
              <h1 className="display-huge text-white max-w-5xl" data-testid="event-title">
                {event.name}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* DETAILS */}
      <section className="relative">
        <div className="dark-content-container py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Description */}
            <Reveal className="lg:col-span-7 space-y-12">
              <div>
                <span className="eyebrow mb-4 block">About</span>
                <h2 className="display-medium text-white mb-6">About This Event</h2>
                <p className="body-large text-white/75 whitespace-pre-line leading-relaxed">
                  {event.fullDesc}
                </p>
              </div>

              {/* Agenda */}
              {event.agenda && event.agenda.length > 0 && (
                <div data-testid="event-agenda-section">
                  <span className="eyebrow mb-4 block">Agenda</span>
                  <h2 className="display-medium text-white mb-6">What to Expect</h2>
                  <div className="space-y-3">
                    {event.agenda.map((item, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-4 surface-2 border border-white/10 rounded-2xl p-5"
                        data-testid={`event-agenda-item-${i}`}
                      >
                        <div className="w-10 h-10 rounded-xl glass-teal flex items-center justify-center flex-shrink-0">
                          <ListChecks className="text-[#00FFD1]" size={18} />
                        </div>
                        <p className="body-large text-white/85">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Speakers */}
              {event.speakers && event.speakers.length > 0 && (
                <div data-testid="event-speakers-section">
                  <span className="eyebrow mb-4 block">Speakers</span>
                  <h2 className="display-medium text-white mb-6">Who You'll Meet</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {event.speakers.map((speaker, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-4 surface-2 border border-white/10 rounded-2xl p-5"
                        data-testid={`event-speaker-item-${i}`}
                      >
                        <div className="w-10 h-10 rounded-xl glass-teal flex items-center justify-center flex-shrink-0">
                          <Mic className="text-[#00FFD1]" size={18} />
                        </div>
                        <p className="body-large text-white/85">{speaker}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Reveal>

            {/* Sidebar */}
            <Reveal delay={150} className="lg:col-span-5">
              <div className="lg:sticky lg:top-28 space-y-5">
                <div className="surface-2 border border-white/10 rounded-3xl overflow-hidden">
                  {[
                    { icon: Calendar, label: 'Date', value: event.date, accent: false },
                    { icon: MapPin, label: 'Venue', value: event.venue, accent: false },
                    ...(event.status === 'past'
                      ? []
                      : [{ icon: Users, label: 'Price', value: event.price, accent: true }]),
                  ].map((row, i, rows) => {
                    const Icon = row.icon;
                    return (
                      <div
                        key={i}
                        className={`flex items-start gap-4 p-6 ${
                          i !== rows.length - 1 ? 'border-b border-white/8' : ''
                        }`}
                      >
                        <div className="w-11 h-11 rounded-xl glass-teal flex items-center justify-center flex-shrink-0">
                          <Icon className="text-[#00FFD1]" size={18} />
                        </div>
                        <div className="flex-1">
                          <p className="body-small text-white/50 mb-1">{row.label}</p>
                          <p
                            className={`body-large font-semibold ${
                              row.accent ? 'text-[#00FFD1]' : 'text-white'
                            }`}
                          >
                            {row.value}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <a
                  href="https://deftexp.fillout.com/lessgo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full"
                  data-testid="event-register-btn"
                >
                  Register Your Interest
                  <ArrowUpRight size={18} />
                </a>

                <p className="body-small text-white/40 text-center px-4">
                  Members only. Register your interest to join the community and access all events.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventDetail;
